import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatCalendar } from '@angular/material/datepicker';
import { Subscription } from 'rxjs';

import { I18nProvider } from '../../providers/i18n/i18n';

@Component({
  selector: 'app-modern-datepicker-header',
  templateUrl: './modern-datepicker-header.component.html',
  styleUrls: ['./modern-datepicker-header.component.scss'],
})
export class ModernDatepickerHeaderComponent implements OnDestroy {
  readonly months: string[];
  readonly years: number[];
  readonly page: any;

  private readonly stateChanges: Subscription;

  constructor(
    public calendar: MatCalendar<Date>,
    private dateAdapter: DateAdapter<Date>,
    changeDetectorRef: ChangeDetectorRef,
    i18n: I18nProvider,
  ) {
    this.months = this.dateAdapter.getMonthNames('long');
    const currentYear = this.dateAdapter.getYear(this.dateAdapter.today());
    const minYear = this.calendar.minDate ? this.dateAdapter.getYear(this.calendar.minDate) : 1940;
    const maxYear = this.calendar.maxDate ? this.dateAdapter.getYear(this.calendar.maxDate) : currentYear + 10;
    this.years = Array.from({length: maxYear - minYear + 1}, (_value, index) => minYear + index);
    this.page = i18n.translate('ProfessoresComponent');
    this.stateChanges = this.calendar.stateChanges.subscribe(() => changeDetectorRef.markForCheck());
  }

  get activeMonth(): number {
    return this.dateAdapter.getMonth(this.calendar.activeDate);
  }

  get activeYear(): number {
    return this.dateAdapter.getYear(this.calendar.activeDate);
  }

  get periodLabel(): string {
    return `${this.months[this.activeMonth]} ${this.activeYear}`;
  }

  get previousDisabled(): boolean {
    return !this.monthIsWithinLimit(this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1), this.calendar.minDate, false);
  }

  get nextDisabled(): boolean {
    return !this.monthIsWithinLimit(this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1), this.calendar.maxDate, true);
  }

  changeMonth(value: string): void {
    this.setActiveDate(this.activeYear, Number(value));
  }

  changeMonthFromEvent(event: Event): void {
    this.changeMonth((event.target as HTMLSelectElement).value);
  }

  changeYear(value: string): void {
    this.setActiveDate(Number(value), this.activeMonth);
  }

  changeYearFromEvent(event: Event): void {
    this.changeYear((event.target as HTMLSelectElement).value);
  }

  previousMonth(): void {
    if (this.previousDisabled) return;
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1);
  }

  nextMonth(): void {
    if (this.nextDisabled) return;
    this.calendar.activeDate = this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1);
  }

  clear(): void {
    this.calendar.selected = null;
    this.calendar.selectedChange.emit(null as any);
  }

  selectToday(): void {
    const today = this.dateAdapter.today();
    this.calendar.activeDate = today;
    this.calendar.selected = today;
    this.calendar.selectedChange.emit(today);
    (this.calendar as any)._userSelection.emit();
  }

  ngOnDestroy(): void {
    this.stateChanges.unsubscribe();
  }

  private setActiveDate(year: number, month: number): void {
    let activeDate = this.dateAdapter.createDate(year, month, 1);

    if (this.calendar.maxDate && activeDate > this.calendar.maxDate) activeDate = this.calendar.maxDate;
    if (this.calendar.minDate && activeDate < this.calendar.minDate) activeDate = this.calendar.minDate;
    this.calendar.activeDate = activeDate;
  }

  private monthIsWithinLimit(date: Date, limit: Date | null, isMaximum: boolean): boolean {
    if (!limit) return true;

    const dateMonth = (this.dateAdapter.getYear(date) * 12) + this.dateAdapter.getMonth(date);
    const limitMonth = (this.dateAdapter.getYear(limit) * 12) + this.dateAdapter.getMonth(limit);
    return isMaximum ? dateMonth <= limitMonth : dateMonth >= limitMonth;
  }
}
